import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "menu", "label", "input", "icon", "option"]

  connect() {
    this.opened = false
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.handleMenuClick = this.onMenuClick.bind(this)
    this.handleWindowChange = this.close.bind(this)
    this.menu = this.menuTarget
    this.originalMenuParent = this.menu.parentNode
    this.originalMenuNextSibling = this.menu.nextSibling

    document.addEventListener("click", this.handleDocumentClick)
    document.addEventListener("keydown", this.handleDocumentKeydown)
    window.addEventListener("resize", this.handleWindowChange)
    window.addEventListener("scroll", this.handleWindowChange, true)
    this.menu.addEventListener("click", this.handleMenuClick)

    this.syncSelectedState()
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick)
    document.removeEventListener("keydown", this.handleDocumentKeydown)
    window.removeEventListener("resize", this.handleWindowChange)
    window.removeEventListener("scroll", this.handleWindowChange, true)
    this.menu.removeEventListener("click", this.handleMenuClick)
    this.restoreMenu()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.opened) {
      this.close()
    } else {
      this.open()
    }
  }

  select(event) {
    event.preventDefault()

    const option = event.currentTarget
    this.inputTarget.value = option.dataset.value
    this.labelTarget.textContent = option.dataset.label
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.syncSelectedState()
    this.close()
    this.buttonTarget.focus()
  }

  open() {
    this.opened = true
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.portalMenu()
    this.positionMenu()
    this.menu.classList.remove("hidden", "pointer-events-none", "opacity-0", "scale-95")
    this.menu.classList.add("pointer-events-auto", "opacity-100", "scale-100")
    this.iconTarget.classList.add("rotate-180")
  }

  close() {
    this.opened = false
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.menu.classList.add("hidden", "pointer-events-none", "opacity-0", "scale-95")
    this.menu.classList.remove("pointer-events-auto", "opacity-100", "scale-100")
    this.restoreMenu()
    this.iconTarget.classList.remove("rotate-180")
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target) || this.menu.contains(event.target)) return

    this.close()
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      return
    }

    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key) && event.target === this.buttonTarget) {
      event.preventDefault()
      this.open()
    }
  }

  onMenuClick(event) {
    const option = event.target.closest("[data-select-target~='option']")
    if (!option || !this.menu.contains(option)) return

    this.select({ preventDefault: () => event.preventDefault(), currentTarget: option })
  }

  portalMenu() {
    if (this.menu.parentNode === document.body) return

    document.body.append(this.menu)
  }

  restoreMenu() {
    this.menu.removeAttribute("style")
    if (this.menu.parentNode !== document.body) return

    if (this.originalMenuParent?.isConnected) {
      this.originalMenuParent.insertBefore(this.menu, this.originalMenuNextSibling)
    } else {
      this.menu.remove()
    }
  }

  positionMenu() {
    const rect = this.buttonTarget.getBoundingClientRect()

    Object.assign(this.menu.style, {
      left: `${rect.left}px`,
      position: "fixed",
      top: `${rect.bottom + 8}px`,
      width: `${rect.width}px`,
      zIndex: "1000"
    })
  }

  syncSelectedState() {
    const selectedValue = this.inputTarget.value.toString()
    const selectedOption = this.optionElements.find((option) => option.dataset.value === selectedValue)

    this.optionElements.forEach((option) => {
      const selected = option.dataset.value === selectedValue
      option.classList.toggle("bg-primary-50", selected)
      option.classList.toggle("text-primary-700", selected)
      option.classList.toggle("font-medium", selected)
      option.setAttribute("aria-selected", String(selected))
    })

    this.labelTarget.textContent = selectedOption?.dataset.label || this.labelTarget.textContent
  }

  get optionElements() {
    return Array.from(this.menu.querySelectorAll("[data-select-target~='option']"))
  }
}
